import { createSlice } from "@reduxjs/toolkit";
import { factory } from "../../api/apiFactory";
import { showNotification } from "./notificationSlice";

const tasksApi = factory.get("tasks");

const initialState = {
  list: {
    loading: false,
    data: [],
    ticket_id: null,
  },
  create: {
    loading: false,
    dialog: false,
  },
  edit: {
    loading: false,
    dialog: false,
    current: null,
  },
  comment: {
    loading: false,
  },
  assignees: {
    loading: false,
    dialog: false,
    current: null,
  },
  actionLoading: false,
  all: {
    loading: false,
    data: [],
    total: 0,
    loaded: false,
    filterDialog: false,
    filters: {
      page: 0,
      skip: 0,
      take: 15,
      status: null,
      department_id: null,
      start_date: null,
      end_date: null,
    },
  },
  details: {
    loading: false,
    data: {},
  },
};

export const tasksSlice = createSlice({
  name: "tasks",
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state[action.payload].loading = !state[action.payload].loading;
    },
    setTasks: (state, { payload }) => {
      state.list.data = payload.data.data;
    },
    setListTicketId: (state, { payload }) => {
      state.list.ticket_id = payload;
    },
    setCreateDialog: (state) => {
      state.create.dialog = !state.create.dialog;
    },
    setEditDialog: (state, { payload }) => {
      state.edit.dialog = !state.edit.dialog;
      state.edit.current = payload || null;
    },
    setAssigneesDialog: (state, { payload }) => {
      state.assignees.dialog = !state.assignees.dialog;
      state.assignees.current = payload || null;
    },
    setActionLoading: (state, { payload }) => {
      state.actionLoading = payload;
    },
    setAllTasks: (state, { payload }) => {
      state.all.data = payload.data.data;
      state.all.total = payload.data.total;
      state.all.loaded = true;
    },
    setAllFilters: (state, { payload }) => {
      state.all.filters = { ...state.all.filters, ...payload };
    },
    resetAllFilters: (state) => {
      state.all.filters = initialState.all.filters;
    },
    setFilterDialog: (state) => {
      state.all.filterDialog = !state.all.filterDialog;
    },
    setTaskDetails: (state, { payload }) => {
      state.details.data = payload;
    },
    setDetailsLoading: (state, { payload }) => {
      state.details.loading = payload;
    },
  },
});

export const {
  setLoading,
  setTasks,
  setListTicketId,
  setCreateDialog,
  setEditDialog,
  setAssigneesDialog,
  setActionLoading,
  setAllTasks,
  setAllFilters,
  resetAllFilters,
  setFilterDialog,
  setTaskDetails,
  setDetailsLoading,
} = tasksSlice.actions;

export default tasksSlice.reducer;

//axios
const index = (ticketId) => async (dispatch, getState) => {
  try {
    const id = ticketId || getState().tasks.list.ticket_id;
    dispatch(setListTicketId(id));
    dispatch(setLoading("list"));
    const res = await tasksApi.index({ ticket_id: id });
    dispatch(setTasks(res));
    dispatch(setLoading("list"));
  } catch (err) {
    dispatch(setLoading("list"));
    dispatch(
      showNotification({
        type: "error",
        message: err.response?.data?.errors?.[0] || "Failed to load tasks",
      })
    );
    throw new Error(err);
  }
};

const indexAll = () => async (dispatch, getState) => {
  try {
    const filters = getState().tasks.all.filters;
    dispatch(setLoading("all"));
    const res = await tasksApi.index({
      ...filters,
      department_id: filters.department_id?.id || null,
    });
    dispatch(setAllTasks(res));
    dispatch(setLoading("all"));
  } catch (err) {
    dispatch(setLoading("all"));
    dispatch(
      showNotification({
        type: "error",
        message: err.response?.data?.errors?.[0] || "Failed to load tasks",
      })
    );
    throw new Error(err);
  }
};

const show = (id) => async (dispatch) => {
  try {
    dispatch(setDetailsLoading(true));
    const res = await tasksApi.showById(id);
    const d = res.data.data;
    dispatch(setTaskDetails(Array.isArray(d) ? d[0] : d));
    dispatch(setDetailsLoading(false));
  } catch (err) {
    dispatch(setDetailsLoading(false));
    dispatch(
      showNotification({
        type: "error",
        message: err.response?.data?.errors?.[0] || "Failed to load task",
      })
    );
    throw new Error(err);
  }
};

// Refetch whichever task views are currently active after a mutation.
const refreshTask = (taskId) => (dispatch, getState) => {
  const s = getState().tasks;
  if (s.details.data?.id === taskId) dispatch(show(taskId));
  if (s.all.loaded) dispatch(indexAll());
  if (s.list.ticket_id) dispatch(index(s.list.ticket_id));
};

const create = (data) => async (dispatch, getState) => {
  try {
    const ticketId = getState().tasks.list.ticket_id;
    dispatch(setLoading("create"));
    const formdata = new FormData();
    formdata.append("ticket_id", ticketId);
    formdata.append("title", data.title);
    if (data.description) {
      formdata.append("description", data.description);
    }
    if (data.assignee_ids?.length > 0) {
      data.assignee_ids.forEach((user) => {
        formdata.append("assignee_ids[]", user.id);
      });
    }
    if (data.attachments?.length > 0) {
      data.attachments.forEach((file) => {
        formdata.append("attachments[]", file);
      });
    }
    await tasksApi.create(formdata);
    dispatch(setLoading("create"));
    dispatch(
      showNotification({
        type: "success",
        message: "Task created successfully",
      })
    );
    dispatch(setCreateDialog());
    dispatch(index(ticketId));
  } catch (err) {
    dispatch(setLoading("create"));
    dispatch(
      showNotification({
        type: "error",
        message: err.response?.data?.errors?.[0] || "Failed to create task",
      })
    );
    throw new Error(err);
  }
};

const update = (taskId, data) => async (dispatch) => {
  try {
    dispatch(setLoading("edit"));
    await tasksApi.update(taskId, {
      title: data.title,
      description: data.description,
    });
    dispatch(setLoading("edit"));
    dispatch(
      showNotification({
        type: "success",
        message: "Task updated successfully",
      })
    );
    dispatch(setEditDialog());
    dispatch(refreshTask(taskId));
  } catch (err) {
    dispatch(setLoading("edit"));
    dispatch(
      showNotification({
        type: "error",
        message: err.response?.data?.errors?.[0] || "Failed to update task",
      })
    );
    throw new Error(err);
  }
};

const remove = (taskId) => async (dispatch) => {
  try {
    dispatch(setActionLoading(true));
    await tasksApi.delete(taskId);
    dispatch(
      showNotification({
        type: "success",
        message: "Task deleted successfully",
      })
    );
    // No auto-refetch: the task is gone. Callers handle navigation/list refresh.
  } catch (err) {
    dispatch(
      showNotification({
        type: "error",
        message: err.response?.data?.errors?.[0] || "Failed to delete task",
      })
    );
    throw new Error(err);
  } finally {
    dispatch(setActionLoading(false));
  }
};

const addAssignees = (taskId, assigneeIds) => async (dispatch) => {
  try {
    dispatch(setLoading("assignees"));
    await tasksApi.addAssignees(taskId, { assignee_ids: assigneeIds });
    dispatch(setLoading("assignees"));
    dispatch(
      showNotification({
        type: "success",
        message: "Assignees added successfully",
      })
    );
    dispatch(refreshTask(taskId));
  } catch (err) {
    dispatch(setLoading("assignees"));
    dispatch(
      showNotification({
        type: "error",
        message: err.response?.data?.errors?.[0] || "Failed to add assignees",
      })
    );
    throw new Error(err);
  }
};

const removeAssignees = (taskId, assigneeIds) => async (dispatch) => {
  try {
    dispatch(setLoading("assignees"));
    await tasksApi.removeAssignees(taskId, { assignee_ids: assigneeIds });
    dispatch(setLoading("assignees"));
    dispatch(
      showNotification({
        type: "success",
        message: "Assignees removed successfully",
      })
    );
    dispatch(refreshTask(taskId));
  } catch (err) {
    dispatch(setLoading("assignees"));
    dispatch(
      showNotification({
        type: "error",
        message: err.response?.data?.errors?.[0] || "Failed to remove assignees",
      })
    );
    throw new Error(err);
  }
};

const close = (taskId) => async (dispatch) => {
  try {
    dispatch(setActionLoading(true));
    await tasksApi.close(taskId);
    dispatch(
      showNotification({
        type: "success",
        message: "Task closed successfully",
      })
    );
    await dispatch(refreshTask(taskId));
  } catch (err) {
    dispatch(
      showNotification({
        type: "error",
        message: err.response?.data?.errors?.[0] || "Failed to close task",
      })
    );
    throw new Error(err);
  } finally {
    dispatch(setActionLoading(false));
  }
};

const addComment = (taskId, data) => async (dispatch) => {
  try {
    dispatch(setLoading("comment"));
    const formdata = new FormData();
    formdata.append("comment", data.comment);
    if (data.attachments?.length > 0) {
      data.attachments.forEach((file) => {
        formdata.append("attachments[]", file);
      });
    }
    await tasksApi.addComment(taskId, formdata);
    dispatch(setLoading("comment"));
    dispatch(
      showNotification({
        type: "success",
        message: "Comment added successfully",
      })
    );
    dispatch(refreshTask(taskId));
  } catch (err) {
    dispatch(setLoading("comment"));
    dispatch(
      showNotification({
        type: "error",
        message: err.response?.data?.errors?.[0] || "Failed to add comment",
      })
    );
    throw new Error(err);
  }
};

export const Tasks = {
  index,
  indexAll,
  show,
  create,
  update,
  remove,
  addAssignees,
  removeAssignees,
  close,
  addComment,
};
